import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { connect } from 'react-redux';
import cookie from 'react-cookies';
import logo from '../../../images/drapcode-logo-white.svg';
import { getBuilderURL } from '../../../common/utils/builder-url-util';
import PublishPopover, { PUBLISH_TRIGGER_ID } from './PublishPopover';
import VersionPopover, { VERSION_TRIGGER_ID } from './VersionPopover';
import { publishVibeCodeOnServer } from '../../../common/utils/publish_utils';
import { WorkspaceChromeContext } from './WorkspaceChromeContext';

export class ProjectHeader extends Component {
  static propTypes = {
    projectId: PropTypes.string.isRequired,
    projectDetail: PropTypes.object,
    version: PropTypes.string,
    versions: PropTypes.array,
    actions: PropTypes.object.isRequired,
    location: PropTypes.object,
    pages: PropTypes.array,
  };

  static defaultProps = {
    pages: [],
  };

  static contextType = WorkspaceChromeContext;

  state = {
    isPublishing: false,
    selVersion: {
      ALPHA: this.props.version,
      BETA: this.props.version,
      PREVIEW: this.props.version,
      PRODUCTION: this.props.version,
    },
    versionOpen: false,
    popoverOpen: false,
    isPreviewPublishing: false,
    pagesOpen: false,
  };

  componentDidMount() {
    const { projectId, version, actions, pages } = this.props;
    if (projectId && actions && actions.loadPages && (!pages || !pages.length)) {
      actions.loadPages({ projectId, version });
    }
  }

  handleVersionChange = (event, type) => {
    const { selVersion } = this.state;
    const {
      target: { value },
    } = event;
    selVersion[type] = value;
    this.setState({ selVersion });
  };

  publishApp = async (envType, name, cleanPublish) => {
    const { projectDetail, actions } = this.props;
    let selVersion = this.state.selVersion[envType];
    if (!selVersion) selVersion = 1;

    const updateState = (status) => {
      this.setState({ isPublishing: status, isPreviewPublishing: status });
    };

    try {
      /**
       * TODO:
       * Add boolean for github push
       */

      await publishVibeCodeOnServer({
        envType,
        name,
        updateState,
        selVersion,
        projectDetail,
        actions,
      });
    } catch (error) {
      console.error('Error during publishing:', error);
      return error;
    }
  };

  handleVersionSwitch = (event) => {
    const { target } = event;
    const { value } = target;
    const { projectId } = this.props;
    const path = location.pathname;
    const extractPath = path.split(/[/]/);
    const route = extractPath[2];
    window.location.href = `/2.0/${route}/${value}/v/${projectId}`;
  };

  isSwalClick = (event) =>
    event &&
    event.type === 'click' &&
    event.target &&
    event.target.closest &&
    event.target.closest('.swal2-container');

  toggle = (event) => {
    if (this.isSwalClick(event)) return;
    this.setState((prev) => {
      const popoverOpen = !prev.popoverOpen;
      return {
        popoverOpen,
        ...(popoverOpen ? { versionOpen: false } : {}),
      };
    });
  };

  toggleVersion = (event) => {
    if (this.isSwalClick(event)) return;
    this.setState((prev) => {
      const versionOpen = !prev.versionOpen;
      return {
        versionOpen,
        ...(versionOpen ? { popoverOpen: false } : {}),
      };
    });
  };

  toggleGlobalSearch = () => {
    window.dispatchEvent(new CustomEvent('toggleGlobalSearch', { detail: { visible: true } }));
  };

  openEditor = () => {
    const { projectId, version } = this.props;
    let token = cookie.load('token');
    if (!token) token = '';
    const redirectUrl = getBuilderURL();
    window.location.href = `${redirectUrl}editor/${version}/v/${projectId}/loader?token=${token}`;
  };

  handleMenu = () => {
    const chrome = this.context || {};
    if (typeof window !== 'undefined' && window.innerWidth <= 768) {
      if (chrome.toggleNav) chrome.toggleNav();
      return;
    }
    if (chrome.toggleSidebar) chrome.toggleSidebar();
  };

  pageEditorHref = (page) => {
    const { version } = this.props;
    const token = cookie.load('token') || '';
    const redirectUrl = getBuilderURL();
    return `${redirectUrl}editor/${version}/v/${page.projectId}/pages/${page.uuid}?token=${token}`;
  };

  renderPagesMenu = () => {
    const { pages } = this.props;
    const { pagesOpen } = this.state;
    const list = Array.isArray(pages) ? pages.filter((page) => page && page.uuid) : [];
    const general = list.filter((page) => !page.isPluginPage);
    const current = general[0] || list[0];
    const label = current && current.name ? current.name : 'Pages';

    return (
      <div className="dc-pages-menu">
        <button
          type="button"
          className="dc-pages-trigger"
          aria-expanded={pagesOpen}
          aria-haspopup="listbox"
          onClick={() => this.setState((prev) => ({ pagesOpen: !prev.pagesOpen }))}
        >
          <span>{label}</span>
          <span aria-hidden="true">▾</span>
        </button>
        {pagesOpen ? (
          <div className="dc-pages-list" role="listbox" aria-label="Pages">
            {list.length ? (
              list.map((page) => (
                <a
                  key={page.uuid}
                  className="dc-pages-option"
                  role="option"
                  href={this.pageEditorHref(page)}
                >
                  {page.name || 'Untitled page'}
                </a>
              ))
            ) : (
              <div className="dc-pages-empty">No pages</div>
            )}
          </div>
        ) : null}
      </div>
    );
  };

  render() {
    const { projectId, projectDetail, version, versions, actions } = this.props;
    const { popoverOpen, versionOpen, isPublishing, isPreviewPublishing, selVersion } = this.state;
    const projectName =
      (projectDetail &&
        (projectDetail.name || projectDetail.projectName || projectDetail.seoName)) ||
      'Project';
    const vibeChrome = this.context && this.context.vibeChrome;
    const codeHref = `/2.0/deployment-setting/${version}/v/${projectId}/code-ui`;

    const saveBusy = Boolean(vibeChrome && vibeChrome.isSaving);
    const saveDirty = Boolean(vibeChrome && vibeChrome.isDirty);

    return (
      <header className="dc-project-header">
        <div className="dc-header-left">
          <button
            type="button"
            className="dc-icon-btn dc-nav-toggle"
            aria-label="Toggle navigation"
            title="Toggle navigation"
            onClick={this.handleMenu}
          >
            <i className="wb webconnect-bars" aria-hidden="true" />
          </button>
          <button type="button" className="dc-brand" title="Goto Editor" onClick={this.openEditor}>
            <img src={logo} alt="DrapCode" />
          </button>
          <div className="dc-project-name" title={projectName}>
            {projectName}
          </div>
        </div>
        <div className="dc-header-center">
          {vibeChrome ? (
            <div className="dc-segment" role="group" aria-label="Workspace">
              <button
                type="button"
                className={`dc-segment-btn${
                  vibeChrome.workspaceMode === 'preview' ? ' is-active' : ''
                }`}
                aria-pressed={vibeChrome.workspaceMode === 'preview'}
                onClick={() => vibeChrome.onModeChange('preview')}
              >
                <i className="wb webconnect-globe" aria-hidden="true" />
                <span>Preview</span>
              </button>
              <button
                type="button"
                className={`dc-segment-btn${
                  vibeChrome.workspaceMode === 'terminal' ? ' is-active' : ''
                }`}
                aria-pressed={vibeChrome.workspaceMode === 'terminal'}
                onClick={() => vibeChrome.onModeChange('terminal')}
              >
                <i className="wb webconnect-terminal" aria-hidden="true" />
                <span>Logs</span>
              </button>
              <button
                type="button"
                className={`dc-segment-btn${
                  vibeChrome.workspaceMode === 'code' ? ' is-active' : ''
                }`}
                aria-pressed={vibeChrome.workspaceMode === 'code'}
                onClick={() => vibeChrome.onModeChange('code')}
              >
                <i className="wb webconnect-code-slash" aria-hidden="true" />
                <span>Code</span>
              </button>
              <button
                type="button"
                className={`dc-segment-btn${
                  vibeChrome.workspaceMode === 'more' ? ' is-active' : ''
                }`}
                aria-pressed={vibeChrome.workspaceMode === 'more'}
                onClick={() => vibeChrome.onModeChange('more')}
              >
                <i className="wb webconnect-grid-fill" aria-hidden="true" />
                <span>More</span>
              </button>
            </div>
          ) : null}
          {vibeChrome && vibeChrome.workspaceMode === 'preview' ? this.renderPagesMenu() : null}
        </div>
        <div className="dc-header-actions">
          {vibeChrome && !saveDirty && !saveBusy ? (
            <span className="dc-save-status">
              <i className="wb webconnect-check2-circle" aria-hidden="true" />
              Saved
            </span>
          ) : null}
          <button
            type="button"
            className="dc-icon-btn"
            title="Search"
            aria-label="Search"
            onClick={this.toggleGlobalSearch}
          >
            <i className="wb webconnect-search" aria-hidden="true" />
          </button>
          {/* <button
            type="button"
            id={VERSION_TRIGGER_ID}
            className="dc-btn dc-btn-ghost dc-version-btn"
            title="Version"
            aria-label={`Version ${version}`}
            aria-haspopup="dialog"
            aria-expanded={versionOpen}
            aria-controls="dc-version-panel"
          >
            <i className="wb webconnect-clock" aria-hidden="true" />
            <span className="dc-version-label">v{version}</span>
          </button> */}
          {vibeChrome && (saveDirty || saveBusy) ? (
            <button
              type="button"
              className="dc-btn dc-btn-secondary"
              disabled={saveBusy || !saveDirty}
              onClick={vibeChrome.onSave}
            >
              {saveBusy ? 'Saving…' : 'Save'}
            </button>
          ) : null}
          {/* <Link className="dc-btn dc-btn-ghost dc-view-code" title="View Code" to={codeHref}>
            <i className="wb webconnect-code-slash" aria-hidden="true" />
            <span className="dc-view-code-label">View Code</span>
          </Link> */}
          {/* <button
            type="button"
            className="dc-icon-btn dc-btn-ghost"
            title="Preview project"
            aria-label="Preview project"
            onClick={this.togglePreviewPopup}
          >
            <i className="wb webconnect-eye" aria-hidden="true" />
            Preview
          </button> */}
          {vibeChrome ? (
            <button
              type="button"
              className="dc-btn dc-btn-secondary dc-ai-toggle"
              aria-expanded={Boolean(vibeChrome.chatOpen)}
              onClick={vibeChrome.onToggleChat}
            >
              AI
            </button>
          ) : null}
          <button
            type="button"
            id={PUBLISH_TRIGGER_ID}
            className="dc-btn dc-btn-primary dc-btn-pill"
            title="Publish"
            aria-label="Publish"
            aria-haspopup="dialog"
            aria-expanded={popoverOpen}
            aria-controls="dc-publish-panel"
          >
            <i className="wb webconnect-rocket" aria-hidden="true" />
            Publish
          </button>
        </div>
        {/* <VersionPopover
          isOpen={versionOpen}
          toggle={this.toggleVersion}
          handleVersionSwitch={this.handleVersionSwitch}
          version={version}
          versions={versions}
        /> */}
        {projectDetail ? (
          <>
            <PublishPopover
              isOpen={popoverOpen}
              toggle={this.toggle}
              projectId={projectId}
              version={version}
              projectDetail={projectDetail}
              versions={versions}
              isPublishing={isPublishing}
              isPreviewPublishing={isPreviewPublishing}
              selVersion={selVersion}
              publishApp={this.publishApp}
              handleVersionChange={this.handleVersionChange}
              actions={actions}
            />
          </>
        ) : null}
      </header>
    );
  }
}

function mapStateToProps(state) {
  const home = state.home || {};
  return {
    pages: home.pages || [],
  };
}

export default connect(mapStateToProps)(ProjectHeader);
